import type { GroupEnrollmentMode, GroupLifecycleStatus } from './types';

export const ENROLLMENT_MODE_OPTIONS: {
  value: GroupEnrollmentMode;
  label: string;
  description: string;
}[] = [
  {
    value: 'closed',
    label: 'Closed',
    description: 'Fixed start and end dates; members enroll before the lock date.',
  },
  {
    value: 'open',
    label: 'Open',
    description: 'Clients may join while the group is active, subject to capacity.',
  },
];

/** Common IANA zones for US practices; free-text still accepted via Select creatable. */
export const COMMON_TIMEZONE_OPTIONS = [
  'America/Los_Angeles',
  'America/Denver',
  'America/Phoenix',
  'America/Chicago',
  'America/New_York',
  'America/Anchorage',
  'Pacific/Honolulu',
  'UTC',
] as const;

export const RECURRENCE_OPTIONS = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Every other week' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'custom', label: 'Custom (pick dates)' },
] as const;

export type GroupRecurrenceOption = (typeof RECURRENCE_OPTIONS)[number]['value'];

export function formatGroupRecurrenceLabel(
  recurrence: GroupRecurrenceOption | undefined,
): string {
  return RECURRENCE_OPTIONS.find((o) => o.value === recurrence)?.label ?? 'Weekly';
}

export function normalizeEnrollmentMode(mode: string | undefined): GroupEnrollmentMode {
  switch (mode) {
    case 'closed':
    case 'closed-fixed-iteration':
      return 'closed';
    case 'open':
    case 'open-rolling':
    case 'continuous':
    case 'single-event':
      return 'open';
    default:
      return 'closed';
  }
}

export function dayOfWeekFromDate(dateStr: string): string {
  const [year, month, day] = dateStr.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString('en-US', { weekday: 'long' });
}

export const GROUP_LIFECYCLE_STATUS_OPTIONS: {
  value: GroupLifecycleStatus | 'all';
  label: string;
}[] = [
  { value: 'all', label: 'All statuses' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

export const GROUP_LIFECYCLE_STATUS_LABELS: Record<GroupLifecycleStatus, string> = {
  active: 'Active',
  inactive: 'Inactive',
};

export const GROUP_LIFECYCLE_STATUS_COLORS: Record<GroupLifecycleStatus, string> = {
  active: 'green',
  inactive: 'gray',
};
